/**
 * Keepalive liveness accounting for the raw API-Gateway WebSocket.
 *
 * The failure this guards against: the socket's TCP/WS stays `OPEN` (so
 * `ws.onclose` never fires) but the server has stopped delivering data — a
 * "half-open" socket after mobile sleep, a network switch, or a silent NAT
 * drop. The adapter believed it was still connected forever, so nothing
 * reconnected and a folded/waiting player went silently dead (blinding down in
 * a tournament with a frozen "Adjusting Tables" overlay up).
 *
 * The client sends a keepalive `ping` on a timer. This module turns that into a
 * liveness check: each keepalive tick, if NOTHING has arrived on the socket
 * since the previous tick, it counts as a miss; after `maxMissedPongs`
 * consecutive misses the socket is treated as dead and the adapter forces a
 * reconnect even though `connected` is still `true`.
 *
 * IMPORTANT (EN-9731 field lesson, echoed from the Unity client): liveness is
 * proven by ANY inbound frame — a game/tournament broadcast, a lobby update, OR
 * a `pong` — NOT by pongs alone. The server's `ping` route does real work (DDB
 * lookup + RDS UPDATE) and only emits `pong` on its success path, so under RDS
 * contention or a lapsed connection-record TTL it can go 403/500/slow while the
 * socket itself is perfectly healthy and still streaming broadcasts. Treating a
 * missed pong as fatal on its own would tear down a live, actively-receiving
 * socket — the exact bug Unity hit when it relied on ping/pong for liveness.
 * So the adapter resets this counter on EVERY message (see onInboundMessage);
 * the ping is only the traffic generator that lets us notice a *silent* socket
 * (no broadcasts AND no pongs) — a genuine half-open connection.
 *
 * Pure + framework-free so the threshold logic is unit-testable without a real
 * socket or timers.
 */

export type PingLiveness = {
  /** A keepalive ping has been sent and no pong has come back yet. */
  pingOutstanding: boolean;
  /** Consecutive keepalive ticks that found the prior ping still unanswered. */
  missedPongs: number;
};

// Two consecutive unanswered keepalive pings ≈ dead. With a 25s keepalive that
// is ~50–75s to detect a silent half-open socket — vs. never before. Kept ≥2 so
// a single slow/dropped pong on a healthy link can't trigger a spurious
// reconnect (a real pong returns in well under a second).
export const MAX_MISSED_PONGS = 2;

export const freshLiveness = (): PingLiveness => ({ pingOutstanding: false, missedPongs: 0 });

/**
 * Account for a keepalive ping about to be sent. If the previous keepalive ping
 * is still outstanding, that's a missed pong. Returns the next state and whether
 * the socket should now be considered dead (force a reconnect).
 */
export const onKeepalivePing = (
  prev: PingLiveness,
  maxMissedPongs: number = MAX_MISSED_PONGS
): { next: PingLiveness; dead: boolean } => {
  const missedPongs = prev.pingOutstanding ? prev.missedPongs + 1 : prev.missedPongs;
  return {
    next: { pingOutstanding: true, missedPongs },
    dead: missedPongs >= maxMissedPongs
  };
};

/**
 * ANY inbound frame arrived (a broadcast, a lobby update, or a pong) → the
 * socket is demonstrably alive; clear the counters. This is the primary
 * liveness signal — see the module note on why pongs alone are not trusted.
 */
export const onInboundMessage = (): PingLiveness => freshLiveness();

/**
 * A pong specifically arrived. Alias of onInboundMessage kept for call sites /
 * tests that name the pong case explicitly; a pong is just one kind of inbound
 * frame and carries no more weight than a broadcast.
 */
export const onPong = onInboundMessage;

/** Socket (re)opened → start liveness accounting from a clean slate. */
export const onOpen = (): PingLiveness => freshLiveness();

/**
 * Whether the socket looks stale right now (a keepalive pong has already been
 * missed) even if it still reports `connected`. Used by the visibility/focus
 * recovery path to force a reconnect the instant the player returns, rather than
 * waiting for the next keepalive tick to reach the dead threshold.
 */
export const isLivenessStale = (state: PingLiveness): boolean => state.missedPongs > 0;
